import React, { useEffect } from 'react';
import { View, Switch, Modal } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import { dispatchService } from '../../../services/central-api/dispatch';
import MyAppText from '../../../components/MyAppText';
import ActingBodiesDropdown from './ActingBodiesDropdown';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { useAuth } from '../../../context/Auth';
import { useForm, Controller } from 'react-hook-form';

interface OccurrenceTypeForm {
    name: string;
    active: boolean;
    actingBodyId: number;
}

export default function OccurrenceTypesModal({ onClose, occurrenceType, isModalVisible }: { onClose: () => void; occurrenceType?: OccurrenceType; isModalVisible: boolean; }) {
    const { user: authenticatedUser } = useAuth();
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const defaultEmptyValues: OccurrenceTypeForm = {
        name: '',
        active: true,
        actingBodyId: 0,
    };

    const form = useForm<OccurrenceTypeForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange',
    });

    useEffect(() => {
        if (!isModalVisible || !authenticatedUser) {
            return;
        }
        if (occurrenceType) {
            form.reset({
                name: occurrenceType.name,
                active: occurrenceType.active,
                actingBodyId: occurrenceType.actingBody.id,
            });
        } else {
            form.reset({
                ...defaultEmptyValues,
                actingBodyId:
                    !(authenticatedUser.isAdmin || authenticatedUser.permissions.dispatch_manage_all_acting_bodies_occurrence_types) && authenticatedUser.actingBodyUnit.actingBodyId
                        ? authenticatedUser.actingBodyUnit.actingBodyId
                        : 0,
            });
        }
    }, [isModalVisible]);

    async function formSubmit() {
        const data = form.getValues();
        try {
            if (occurrenceType?.id) {
                await dispatchService.updateOccurrenceType({
                    id: occurrenceType.id,
                    name: data.name,
                    active: data.active,
                });
            } else {
                await dispatchService.createOccurrenceType({
                    name: data.name,
                    active: data.active,
                    actingBodyId: data.actingBodyId !== 0 ? data.actingBodyId : undefined,
                });
            }
            onClose();
        } catch (err) {
            console.error(err);
        }
    }

    return (
        <Modal
            transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={onClose}>
            <View style={styles.modalContainer}>
                <View style={styles.centeredView}>
                    <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: '20%' }]}>
                        {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_occurrence_types) &&
                            <ActingBodiesDropdown
                                includeAllOption={false}
                                form={form}
                                fieldName='actingBodyId'
                                zIndex={2}
                                disabled={!!occurrenceType?.id}
                            />
                        }

                        <FormInput
                            label={translate('name')}
                            placeholder={translate('name')}
                            form={form}
                            fieldName='name'
                            required={true}
                        />

                        <View>
                            <MyAppText style={styles.generalInformationTitleText}>
                                {translate('active')}
                            </MyAppText>
                            <Controller
                                control={form.control}
                                name='active'
                                render={({ field: { value, onChange } }) => (
                                    <Switch
                                        trackColor={{ false: '#767577', true: '#81b0ff' }}
                                        thumbColor={value ? '#f5dd4b' : '#f4f3f4'}
                                        onValueChange={onChange}
                                        value={value}
                                    />
                                )}
                            />
                        </View>

                        <FormActions
                            onSubmit={formSubmit}
                            onClose={onClose}
                            disabled={!form.formState.isValid}
                        />
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    formContainer: {
        minWidth: 300,
        minHeight: 150,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25
    },
    generalInformationTitleText: {
        color: theme.colors.textColor
    },
    modalContainer: {
        flex: 1,
        backgroundColor: '#31313199'
    },
    centeredView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    buttonsContainer: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 10,
    },
}));
